import React, { useState, useEffect, useRef, useCallback} from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  TextField
} from '@mui/material';
import ActionButtons from './ActionButtons';
import useInlineComments from '../../hooks/useInlineComments';
import CommentThreadPanel from '../comments/CommentThreadPanel';
import TextSelectionPopup from '../comments/TextSelectionPopup';
import MarkdownRenderer from '../common/MarkdownRenderer';
import useNotification from '../../hooks/useNotification';
import useValidation from '../../hooks/useValidation';
import TaskApprovalBar from './TaskApprovalBar';
import { ValidatedIcon, UnvalidatedIcon } from '../icons/ValidationIcons';
import { Accordion, ScrollableBox, EditableTextField } from '../atoms';
import LoadMoreButton from '../atoms/LoadMore';
import EmptyStateBox from '../atoms/EmptyStateBox';
import LoadingBox from '../atoms/LoadingBox';
import useSectionCRUD from '../../hooks/useSectionCRUD';
import useContentActions from '../../hooks/useContentActions';
import useExplain from '../../hooks/useExplain';
import useImprove from '../../hooks/useImprove';
import useSecurityGuidelinesVersions from '../../hooks/useSecurityGuidelinesVersions';
import VersionSelector from '../atoms/VersionSelector';
import ExplanationModal from '../atoms/ExplanationModal';
import ImprovementModal from '../atoms/ImprovementModal';
import InlineImproveInput from '../common/InlineImproveInput';
import useFullScreen from '../../hooks/useFullScreen';
import TopBarButtons from '../atoms/TopBarButtons';
import ConfirmationDialog from '../common/ConfirmationDialog';
import SectionHeader from '../atoms/Header';
import conversationService from '../../services/conversationService';
import { capitalizeFirst } from '../../utils/textTransformations';
import AddSectionModal from '../atoms/AddSectionModal';
import useParsedSections from '../../hooks/useParsedSections';

// Import Mermaid for diagram rendering
import mermaid from 'mermaid';

// Initialize mermaid config
try {
  mermaid.initialize({
    startOnLoad: false,
    theme: 'default',
    securityLevel: 'loose',
    flowchart: { useMaxWidth: false, htmlLabels: true },
    sequence: { diagramMarginX: 50, diagramMarginY: 10 },
    classDiagram: { useMaxWidth: false },
    er: { useMaxWidth: false },
    gantt: { useMaxWidth: false }
  });
} catch (error) {
  console.error('Error initializing mermaid:', error);
}

// Mermaid diagram component for Security Guidelines
const MermaidDiagram = ({ code, index }) => {
  const diagramRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const renderDiagram = async () => {
      try {
        if (diagramRef.current && code.trim()) {
          diagramRef.current.innerHTML = '';
          const diagramId = `diagram-security-${index}-${Date.now()}`;
          const { svg } = await mermaid.render(diagramId, code.trim());
          diagramRef.current.innerHTML = svg;
          setIsLoading(false);
        }
      } catch (error) {
        console.error(`Error rendering Security Mermaid diagram:`, error);
        setError(error.message);
        setIsLoading(false);
        if (diagramRef.current) {
          diagramRef.current.innerHTML = `<pre style="margin: 0; color: #666; font-size: 12px; padding: 10px;">Error rendering diagram:\n${code}</pre>`;
        }
      }
    };

    setTimeout(renderDiagram, 100);
  }, [code, index]);

  return (
    <Box sx={{
      p: 2,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '200px',
      overflow: 'auto'
    }}>
      {isLoading && !error && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={24} />
          <Typography variant="caption" color="text.secondary">
            Rendering security diagram...
          </Typography>
        </Box>
      )}
      <div
        ref={diagramRef}
        className="mermaid-diagram"
        style={{
          width: '100%',
          height: 'auto',
          textAlign: 'center',
          display: isLoading && !error ? 'none' : 'block'
        }}
      />
    </Box>
  );
};

// Format section title to sentence case
const formatSectionTitleToSentenceCase = (title) => {
  if (!title) return title;
  return capitalizeFirst(title);
};

/**
 * A split-view layout component that shows conversation on the left
 * and security guidelines in a structured format on the right
 */
const SplitViewSecurityGuidelines = ({ conversation, children, isStreaming, streamingContent, projectId, onConversationUpdate }) => {
  const { showNotification } = useNotification();
  const securityPanelRef = useRef(null);

  const isLocalUpdateRef = useRef(false);

  // Use version management for security guidelines - pass isLocalUpdateRef
  const {
    versions,
    currentVersion,
    currentVersionIndex,
    setCurrentVersionIndex,
    hasVersions,
    isLatestVersion,
    updateCurrentVersion
  } = useSecurityGuidelinesVersions(conversation, isStreaming, streamingContent, isLocalUpdateRef);

  // useParsedSections hook — single source of truth for section data
  const {
    sections: parsedSections,
    setSections: setParsedSections,
    groupedSections,
    isLoading: isSectionsLoading,
    contentRef,
    refresh: refreshSections,
    markLocalEdit,
  } = useParsedSections({ conversation, isStreaming, streamingContent });

  // State for managing expanded/visible sections
  const [expandedSections, setExpandedSections] = useState({});

  // Section highlight from search navigation (?section=sectionId)
  const useSectionHighlight = require('../../hooks/useSectionHighlight').default;
  const { highlightRef, shouldHighlight } = useSectionHighlight(expandedSections, setExpandedSections);
  const [visibleSections, setVisibleSections] = useState(10);
  const [showAddModal, setShowAddModal] = useState(false);

  // Track previous version index to detect actual version changes
  const prevVersionIndexRef = useRef(currentVersionIndex);

  // Version switching: reset visible sections when switching versions
  useEffect(() => {
    if (currentVersion) {
      if (prevVersionIndexRef.current !== currentVersionIndex) {
        setVisibleSections(10);
        prevVersionIndexRef.current = currentVersionIndex;
      }
    }
  }, [currentVersion, currentVersionIndex]);

  // Check if this is a security guidelines conversation
  const isSecurityGuidelinesConversation = conversation && (
    (conversation?.taskId || conversation?.task_id) === 'security-guidelines' ||
    (conversation?.taskId || conversation?.task_id) === 'security-testing'
  );

  const showSecurityPanel = parsedSections.length > 0;

  const handleSectionToggle = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleLoadMore = () => {
    setVisibleSections(prev => Math.min(prev + 10, parsedSections.length));
  };

  const handleShowAll = () => {
    setVisibleSections(parsedSections.length);
  };

  // Use CRUD operations hook
  const {
    editingSection,
    editedContent,
    editedTitle,
    isSaving,
    deleteConfirmation,
    setEditedContent,
    setEditedTitle,
    setDeleteConfirmation,
    handleStartEdit: handleStartEditBase,
    handleCancelEdit,
    handleSaveEdit: handleSaveEditOriginal,
    handleDeleteSection: handleDeleteSectionClick,
    handleCopySection
  } = useSectionCRUD(
    conversation,
    parsedSections,
    setParsedSections,
    setExpandedSections,
    contentRef,
    updateCurrentVersion,
    currentVersion,
    isLocalUpdateRef,
    onConversationUpdate,
    markLocalEdit
  );

  // Wrapper to extract content from section object
  const handleStartEdit = (sectionId, section, title) => {
    const contentToEdit = section.rawContent || section.content || '';
    handleStartEditBase(sectionId, contentToEdit, title);
  };

  // Custom delete handler for security guidelines
  const handleDeleteConfirm = async () => {
    const section = deleteConfirmation.section;
    setDeleteConfirmation({ open: false, section: null });

    if (!section) return;

    try {
      // Remove the section from the sections array
      const updatedSections = parsedSections.filter(s => s.id !== section.id);

      // Set the flag to prevent fetch from overwriting local edit
      markLocalEdit();
      isLocalUpdateRef.current = true;
      setParsedSections(updatedSections);

      // Remove expanded state for this section
      setExpandedSections(prev => {
        const updated = { ...prev };
        delete updated[section.id];
        return updated;
      });

      // Reconstruct the full content
      let updatedContent = '';
      updatedSections.forEach((sec, idx) => {
        if (idx > 0) updatedContent += '\n\n';
        const headerPrefix = '#'.repeat(sec.level || 3);
        updatedContent += `${headerPrefix} ${sec.title}\n\n${sec.content}`;
      });

      contentRef.current = updatedContent.trim();

      // Update the current version
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          content: updatedContent.trim(),
          sections: updatedSections
        });
      }

      // Save to backend
      const updatedMessages = [...conversation.messages];
      const currentVersionMessageId = currentVersion?.id;
      let messageUpdated = false;

      if (currentVersionMessageId) {
        for (let i = 0; i < updatedMessages.length; i++) {
          if (updatedMessages[i]._id === currentVersionMessageId ||
              updatedMessages[i].id === currentVersionMessageId) {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent.trim()
            };
            messageUpdated = true;
            break;
          }
        }
      }

      // Fallback: update the last assistant message
      if (!messageUpdated) {
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].role === 'assistant') {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent.trim()
            };
            break;
          }
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      // Update left panel (conversation messages)
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id);
      }

      showNotification({
        message: `"${section.title}" deleted successfully`,
        type: 'success'
      });

    } catch (error) {
      console.error('Error deleting section:', error);
      showNotification({
        message: `Failed to delete "${section.title}"`,
        type: 'error'
      });
    }
  };

  // Use explain hook
  const {
    isExplaining,
    explanationContent,
    showExplanationModal,
    currentSection: explainSection,
    requestExplanation,
    closeExplanationModal
  } = useExplain(projectId);

  // Use improve hook
  const {
    isImproving,
    improvedContent,
    showImprovementModal,
    currentSection: improveSection,
    showImproveInput,
    improvePrompt,
    setImprovePrompt,
    handleImproveClick,
    handleSendImprove,
    handleCancelImprove,
    closeImprovementModal,
    registerSectionRef,
    isSpecificSectionImprovement,
    specificSectionName
  } = useImprove(projectId);

  // Use content actions hook
  const {
    isExporting,
    handleExport,
    getExportContent,
    handleCopy,
    handleRefresh
  } = useContentActions(contentRef, 'Security Guidelines', projectId, conversation?._id);

  // Add full screen functionality
  const {
    isFullScreen,
    toggleFullScreen,
    getLeftPanelStyles,
    getRightPanelStyles,
    getContentStyles
  } = useFullScreen();

  // Initialize validation state
  const initialValidationState = parsedSections.reduce((acc, section) => {
    const versionSpecificId = `${section.id}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    acc[versionSpecificId] = false;
    return acc;
  }, {});

  // Use the validation hook with version-specific IDs
  const {
    validatedItems: validatedSections,
    handleValidateItem: handleValidateSectionBase,
    isLoading: isLoadingValidations,
    currentUserMemberType,
    isAssignedValidator,
    getValidatorType,
  } = useValidation({
    conversationId: conversation?._id,
    projectId,
    taskId: 'security-guidelines',
    initialState: initialValidationState
  });

  // Section content refs for text selection
  const sectionContentRefs = React.useRef({});
  const getSectionContentRef = (sectionId) => {
    if (!sectionContentRefs.current[sectionId]) {
      sectionContentRefs.current[sectionId] = { current: null };
    }
    return sectionContentRefs.current[sectionId];
  };

  // Inline comments
  const {
    commentCounts,
    sectionComments,
    openSectionId: commentOpenSectionId,
    openCommentPanel,
    closeCommentPanel,
    addComment,
    replyToComment,
    resolveComment: resolveCommentAction,
    reopenComment: reopenCommentAction,
    deleteComment: deleteCommentAction,
    isLoading: isCommentsLoading,
  } = useInlineComments({
    projectId,
    conversationId: conversation?._id,
    taskId: 'security-guidelines',
  });

  // Wrapper function to handle version-specific validation
  const handleValidateSection = async (sectionId) => {
    const versionSpecificId = `${sectionId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
    return handleValidateSectionBase(versionSpecificId);
  };

  // Handler for explaining section
  const handleExplainSection = (section) => {
    requestExplanation(section);
  };

  // Handler for improving section
  const handleImproveSection = (section) => {
    handleImproveClick(section);
  };

  // Handle update from improvement modal
  const handleUpdateImprovedContent = async () => {
    if (!improveSection || !improvedContent) return;

    try {
      // Handle remove action — delete section from artifact
      if (improveSection.action === 'remove') {
        
      // Persist to ParsedResponse so changes survive refresh
      try {
        const ts = conversation?.messages?.find(m => m.role === 'assistant')?.createdAt;
        if (ts) {
          const conversationService = (await import('../../services/conversationService')).default;
          await conversationService.updateParsedSections(
            conversation._id || conversation.id,
            ts,
            [] // empty — backend will reconstruct from the saved conversation message
          );
        }
      } catch (e) { console.warn('ParsedResponse save failed:', e); }

      closeImprovementModal();
        showNotification({ message: `Removed: ${improveSection.title}`, type: 'success' });
        // Trigger a conversation refresh to pick up the change
        if (typeof onConversationUpdate === 'function') onConversationUpdate();
        return;
      }


      const contentToUse = improveSection.mergedContent || improvedContent;

      // Update the section with improved content
      const updatedSections = [...parsedSections].map(s => {
        if (s.id === improveSection.id) {
          return {
            ...s,
            content: contentToUse,
            rawContent: contentToUse
          };
        }
        return s;
      });

      // Set the flag to prevent fetch from overwriting local edit
      markLocalEdit();
      isLocalUpdateRef.current = true;
      setParsedSections(updatedSections);

      // Reconstruct the full content
      let updatedContent = '';
      updatedSections.forEach((sec, idx) => {
        if (idx > 0) updatedContent += '\n\n';
        const headerPrefix = '#'.repeat(sec.level || 3);
        updatedContent += `${headerPrefix} ${sec.title}\n\n${sec.content}`;
      });

      contentRef.current = updatedContent.trim();

      // Update the current version
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          sections: updatedSections,
          content: updatedContent.trim(),
          parsedContent: updatedSections
        });
      }

      // Save to backend
      const updatedMessages = [...conversation.messages];
      for (let i = updatedMessages.length - 1; i >= 0; i--) {
        if (updatedMessages[i].role === 'assistant') {
          updatedMessages[i] = {
            ...updatedMessages[i],
            content: updatedContent.trim()
          };
          break;
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      // Update left panel (conversation messages)
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id);
      }

      closeImprovementModal();

      showNotification({
        message: 'Section improved successfully',
        type: 'success'
      });

    } catch (error) {
      showNotification({
        message: 'Failed to update improved content',
        type: 'error'
      });
    }
  };

  // Handler for adding new section
  const handleAddNewSection = async (newSection) => {
    try {
      // Create section ID from title
      const sectionId = newSection.title.toLowerCase().replace(/[^a-z0-9]+/g, '-');

      // Create the new section object with proper structure
      const newSectionData = {
        id: sectionId,
        title: newSection.title,
        content: newSection.content,
        rawContent: newSection.content,
        level: 3, // h3 accordion section
        isCategory: false
      };

      // Add to sections array
      const updatedSections = [...parsedSections, newSectionData];

      // Set the flag to prevent fetch from overwriting local edit
      markLocalEdit();
      isLocalUpdateRef.current = true;
      setParsedSections(updatedSections);

      // Expand the newly added section
      setExpandedSections(prev => ({
        ...prev,
        [sectionId]: true
      }));

      // Reconstruct the full markdown content
      let updatedContent = '';
      updatedSections.forEach((sec, idx) => {
        if (idx > 0) updatedContent += '\n\n';
        const headerPrefix = '#'.repeat(sec.level || 3);
        updatedContent += `${headerPrefix} ${sec.title}\n\n${sec.content}`;
      });

      contentRef.current = updatedContent.trim();

      // Update the current version
      if (updateCurrentVersion && currentVersion) {
        updateCurrentVersion({
          ...currentVersion,
          sections: updatedSections,
          content: updatedContent.trim(),
          parsedContent: updatedSections
        });
      }

      // Save to backend
      const updatedMessages = [...conversation.messages];
      const currentVersionMessageId = currentVersion?.id;
      let messageUpdated = false;

      if (currentVersionMessageId) {
        for (let i = 0; i < updatedMessages.length; i++) {
          if (updatedMessages[i]._id === currentVersionMessageId ||
              updatedMessages[i].id === currentVersionMessageId) {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent.trim()
            };
            messageUpdated = true;
            break;
          }
        }
      }

      // Fallback: update the last assistant message
      if (!messageUpdated) {
        for (let i = updatedMessages.length - 1; i >= 0; i--) {
          if (updatedMessages[i].role === 'assistant') {
            updatedMessages[i] = {
              ...updatedMessages[i],
              content: updatedContent.trim()
            };
            break;
          }
        }
      }

      const conversationData = {
        messages: updatedMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          attachments: msg.attachments || [],
          createdAt: msg.createdAt || new Date().toISOString()
        }))
      };

      await conversationService.saveConversation(conversation._id, conversationData);

      // Update left panel (conversation messages)
      if (onConversationUpdate) {
        await onConversationUpdate(conversation._id);
      }

      showNotification({
        message: `"${newSection.title}" added successfully`,
        type: 'success'
      });

      // Auto-scroll to the new section
      setTimeout(() => {
        if (securityPanelRef.current) {
          securityPanelRef.current.scrollTop = securityPanelRef.current.scrollHeight;
        }
      }, 100);

    } catch (error) {
      console.error('Error adding new section:', error);
      showNotification({
        message: 'Failed to add new section',
        type: 'error'
      });
      throw error;
    }
  };

  // Auto-scroll security panel to bottom when streaming content updates
  useEffect(() => {
    if (isStreaming && securityPanelRef.current && isSecurityGuidelinesConversation) {
      setTimeout(() => {
        if (securityPanelRef.current) {
          securityPanelRef.current.scrollTop = securityPanelRef.current.scrollHeight;
        }
      }, 50);
    }
  }, [streamingContent, isStreaming, isSecurityGuidelinesConversation]);

  // Don't show the split view if this isn't a security guidelines conversation
  if (!isSecurityGuidelinesConversation || (!showSecurityPanel && !isStreaming)) {
    return <>{children}</>;
  }

  // Render the split view
  return (
    <Box sx={{
      position: 'relative',
      width: '100%',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      mt: 5,
    }}>
      {/* Main content with conditional split */}
      <Box sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        flexDirection: { xs: 'column', md: 'row' },
        flex: 1,
        overflow: 'hidden'
      }}>
        {/* Main conversation panel */}
        <Box sx={{
          ...getLeftPanelStyles(false),
          p: 2,
        }}>
          {children}
        </Box>

        {/* Security guidelines panel */}
        <Box
          ref={securityPanelRef}
          sx={{
            ...getRightPanelStyles(false),
            backgroundColor: '#f7f7f7',
            height: '97%',
            p: 2
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...getContentStyles({}),
              borderRadius: "8px",
              backgroundColor: 'white',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: 3,
              pb: 2,
              backgroundColor: 'white',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <SectionHeader label='Security Guidelines' />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {hasVersions && versions.length > 1 && (
                  <VersionSelector
                    versions={versions}
                    currentVersionIndex={currentVersionIndex}
                    onVersionChange={(newIndex) => {
                      // Reset local update flag when user explicitly switches versions
                      isLocalUpdateRef.current = false;
                      setCurrentVersionIndex(newIndex);
                    }}
                    disabled={isStreaming}
                  />
                )}
                <TopBarButtons
                  isFullScreen={isFullScreen}
                  onToggleFullScreen={toggleFullScreen}
                  getExportContent={getExportContent}
                  onExport={handleExport}
                  isExporting={isExporting}
                  onCopy={handleCopy}
                  onRefresh={handleRefresh}
                  onAdd={() => setShowAddModal(true)}
                  disabled={!contentRef.current}
                  exportTooltip="Export as markdown"
                  copyTooltip="Copy to clipboard"
                  refreshTooltip="Refresh diagrams"
                  addTooltip="Add new security guideline"
                />
              </Box>
            </Box>


            {/* Task-level approval tracking */}
            <TaskApprovalBar
              projectId={projectId}
              taskId="security-guidelines"
              conversationId={conversation?._id || conversation?.id}
              validatedItems={validatedSections}
              totalItems={parsedSections ? parsedSections.length : 0}
              currentUserMemberType={currentUserMemberType}
            />
            {/* Show parsed sections or a placeholder if none found yet */}
            {isStreaming ? (
              <LoadingBox message='Generating security guidelines...' spinnerSize={40} />
            ) : showSecurityPanel && parsedSections.length > 0 ? (
              <Box sx={{
                p: 3,
                pt: 2,
                flex: 1,
                overflow: "auto",
                fontSize: '16px',
                '& .MuiTypography-root': {
                  fontSize: '16px !important',
                },
                '& .MuiTypography-body1': {
                  fontSize: '16px !important',
                },
                '& .MuiTypography-body2': {
                  fontSize: '16px !important',
                },
                '& p': {
                  fontSize: '16px !important',
                },
                '& li': {
                  fontSize: '16px !important',
                },
                '& span': {
                  fontSize: '16px !important',
                },
                '& code': {
                  fontSize: '14px !important',
                },
                '& pre': {
                  fontSize: '14px !important',
                },
                '&::-webkit-scrollbar': {
                  width: '8px',
                  backgroundColor: 'transparent',
                },
                '&::-webkit-scrollbar-track': {
                  borderRadius: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  marginTop: '5px',
                  marginBottom: '5px',
                },
                '&::-webkit-scrollbar-thumb': {
                  borderRadius: '10px',
                  backgroundColor: 'rgba(0, 0, 0, 0.2)',
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.4)',
                  },
                },
                '&::-webkit-scrollbar-corner': {
                  backgroundColor: 'transparent',
                },
              }}>
                {/* Render sections as accordions grouped by category */}
                {(() => {
                  const groups = groupedSections;
                  let globalIndex = 0;
                  return groups.slice(0, visibleSections).map((group, groupIndex) => (
                    <Box key={group.categoryId} sx={{ mb: 3 }}>
                      {/* Category Header */}
                      {group.category && (
                        <Box sx={{ mt: groupIndex === 0 ? 0 : 2, mb: 1.5 }}>
                          <SectionHeader label={group.category} />
                        </Box>
                      )}

                      {/* Category section content */}
                      {group.categorySection && (
                        <Box sx={{ pl: 2, mb: 2 }}>
                          <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                            <MarkdownRenderer content={group.categorySection.content || ''} />
                          </Box>
                        </Box>
                      )}

                      {/* Child sections */}
                      <Box sx={{ pl: group.category ? 2 : 0 }}>
                        {group.sections.map((section) => {
                          const idx = globalIndex++;
                          const sectionId = section.id;
                          const versionSpecificId = `${sectionId}_vt${currentVersion?.timestamp || currentVersionIndex}`;
                          const isValidated = validatedSections[versionSpecificId] || false;

                          return (
                            <Accordion
                              key={sectionId}
                              ref={(el) => { registerSectionRef(sectionId, el); if (shouldHighlight(sectionId)) highlightRef.current = el; }}
                              title={formatSectionTitleToSentenceCase(section.title)}
                              defaultExpanded={false}
                              expanded={expandedSections[sectionId]}
                              onChange={(_, expanded) => handleSectionToggle(sectionId)}
                              titleContent={editingSection === sectionId ? (
                                <Box title="" onClick={(e) => e.stopPropagation()} sx={{ maxWidth: '93%', width: '93%' }}>
                                  <TextField
                                    value={editedTitle}
                                    onChange={(e) => setEditedTitle(e.target.value)}
                                    onKeyDown={(e) => e.stopPropagation()}
                                    onFocus={(e) => e.target.removeAttribute('title')}
                                    size="small"
                                    fullWidth
                                    placeholder="Enter security guideline title"
                                    variant="outlined"
                                    InputProps={{ title: '' }}
                                    inputProps={{ title: '', autoComplete: 'off' }}
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        backgroundColor: '#fff',
                                        '& fieldset': { borderWidth: '1px' },
                                        '&:hover fieldset': { borderWidth: '1px' },
                                        '&.Mui-focused fieldset': { borderWidth: '1px' },
                                      },
                                      '& .MuiOutlinedInput-input': {
                                        fontSize: '14px',
                                        fontFamily: 'inherit',
                                        padding: '8px 12px',
                                      },
                                    }}
                                  />
                                </Box>
                              ) : null}
                              rightIcon={
                                <Box
                                  onClick={async (e) => {
                                    e.stopPropagation();

                                    try {
                                      await handleValidateSection(sectionId);
                                    } catch (error) {
                                      showNotification({
                                        message: `Failed to update validation for "${section.title}"`,
                                        type: "error"
                                      });
                                    }
                                  }}
                                  sx={{ display: 'flex', alignItems: 'center', cursor: isAssignedValidator ? 'pointer' : 'default', opacity: isAssignedValidator ? 1 : 0.4, pointerEvents: isAssignedValidator ? 'auto' : 'none' }}
                                >
                                  {isValidated ? (
                                    <ValidatedIcon size={25} memberType={getValidatorType(versionSpecificId)} />
                                  ) : (
                                    <UnvalidatedIcon size={25} />
                                  )}
                                </Box>
                              }
                            >
                              {/* Section content */}
                              <ScrollableBox
                                minHeight="220px"
                                sx={{
                                  textAlign: 'justify',
                                  cursor: editingSection === sectionId ? 'default' : 'pointer',
                                }}
                              >
                                {editingSection === sectionId ? (
                                  <EditableTextField
                                    value={editedContent}
                                    onChange={(e) => setEditedContent(e.target.value)}
                                  />
                                ) : (
                                  <Box ref={getSectionContentRef(section.id)}>
                                    <MarkdownRenderer content={section.content || section.rawContent || ''} />
                                    <TextSelectionPopup
                                      containerRef={getSectionContentRef(section.id)}
                                      sectionId={section.id}
                                      onSubmitComment={addComment}
                                      disabled={editingSection === section.id}
                                    />

                                    {section.content && section.content.includes('```mermaid') && (
                                      <Box sx={{ mt: 2 }}>
                                        {(() => {
                                          const mermaidMatch = section.content.match(/```mermaid\n([\s\S]*?)```/);
                                          if (mermaidMatch && mermaidMatch[1]) {
                                            return <MermaidDiagram code={mermaidMatch[1]} index={idx} />;
                                          }
                                          return null;
                                        })()}
                                      </Box>
                                    )}

                                    {showImproveInput === sectionId && (
                                      <InlineImproveInput
                                        improvePrompt={improvePrompt}
                                        onPromptChange={setImprovePrompt}
                                        onSend={() => handleSendImprove(section)}
                                        onCancel={handleCancelImprove}
                                        promptText="How would you like to improve this section?"
                                        placeholder="Describe the improvements you'd like..."
                                      />
                                    )}
                                  </Box>
                                )}
                              </ScrollableBox>

                              {/* Action buttons */}
                              <Box sx={{
                                position: 'absolute',
                                right: 10,
                                top: 10,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5
                              }}>
                                <ActionButtons
                                  item={section}
                                  itemIndex={idx}
                                  featureId={sectionId}
                                  editingFeature={editingSection}
                                  isSaving={isSaving}
                                  onEdit={() => handleStartEdit(sectionId, section, section.title || '')}
                                  onSave={() => handleSaveEditOriginal(sectionId)}
                                  onCancel={handleCancelEdit}
                                  onImprove={() => handleImproveSection(section)}
                                  onExplain={() => handleExplainSection(section)}
                                  onCopy={() => handleCopySection(section)}
                                  onDelete={() => handleDeleteSectionClick(section)}
                                  vertical={true}
                          onComment={() => openCommentPanel(sectionId)}
                          commentCount={commentCounts[sectionId]?.open || 0}
                          isApproved={isValidated}
                          approvedByType={isValidated ? getValidatorType(versionSpecificId) : null}
                                />
                              </Box>
                            </Accordion>
                          );
                        })}
                      </Box>
                    </Box>
                  ));
                })()}

                {/* Load more button for lazy loading */}
                {(() => {
                  const groups = groupedSections;
                  return groups.length > visibleSections && (
                    <LoadMoreButton
                      remaining={groups.length - visibleSections}
                      onLoadMore={handleLoadMore}
                      onShowAll={handleShowAll}
                      label="Load more"
                    />
                  );
                })()}

              </Box>
            ) : (
              <EmptyStateBox message="No security guidelines found in this conversation yet." />
            )}
          </Paper>
        </Box>
      </Box>

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteConfirmation.open}
        onClose={() => setDeleteConfirmation({ open: false, section: null })}
        title="Delete section"
        message={`Are you sure you want to delete "${deleteConfirmation.section?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        severity="warning"
        height="auto"
        onConfirm={handleDeleteConfirm}
      />

      {/* Explanation Modal */}
      <ExplanationModal
        open={showExplanationModal}
        onClose={closeExplanationModal}
        title={explainSection?.title || ''}
        explanation={explanationContent}
        isLoading={isExplaining}
      />

      {/* Improvement Modal */}
      <ImprovementModal
        open={showImprovementModal}
        onClose={closeImprovementModal}
        onUpdate={handleUpdateImprovedContent}
        section={improveSection}
        improvedContent={improvedContent}
        isGenerating={isImproving}
        isSpecificSection={isSpecificSectionImprovement}
        specificSectionName={specificSectionName}
      />

      {/* Add Section Modal */}
      <AddSectionModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSave={handleAddNewSection}
        sectionType="security-guideline"
        existingSections={parsedSections}
      />

    

      {/* Inline Comment Thread Panel */}
      <CommentThreadPanel
        open={!!commentOpenSectionId}
        onClose={closeCommentPanel}
        sectionTitle={
          (parsedSections || []).find(s => s.id === commentOpenSectionId)?.title || commentOpenSectionId
        }
        comments={sectionComments}
        isLoading={isCommentsLoading}
        onAddComment={(content) => addComment({ sectionId: commentOpenSectionId, content })}
        onReply={replyToComment}
        onResolve={resolveCommentAction}
        onReopen={reopenCommentAction}
        onDelete={deleteCommentAction}
      />
</Box>
  );
};

export default SplitViewSecurityGuidelines;
